import { klarityExtensions } from "@klarity/schema";
import type { AnyExtension, Extensions, Node, NodeViewRenderer } from "@tiptap/core";
import { Dropcursor, Gapcursor, Placeholder, UndoRedo } from "@tiptap/extensions";
import { BlockHandle } from "./block-handle";
import { type DocLinkItem, type EditorHost, HostExtension } from "./host";
import { KlarityInputRules } from "./input-rules";
import { FormatKeymap } from "./keymap";
import { MoveBlockKeymap } from "./move-block";
import { NODE_VIEWS } from "./nodeviews";
import { KlarityPaste } from "./paste";
import type { SlashItem } from "./slash-items";
import { StructureKeymap } from "./structure";
import { DocLinkSuggest, SlashCommand } from "./suggest";
import { createSuggestionStore, type SuggestionStore } from "./suggestion-store";

export type EditorStores = { slash: SuggestionStore<SlashItem>; links: SuggestionStore<DocLinkItem> };

export function createEditorStores(): EditorStores {
  return { slash: createSuggestionStore<SlashItem>(), links: createSuggestionStore<DocLinkItem>() };
}

export function withNodeViews(exts: Extensions, views: Record<string, NodeViewRenderer>): Extensions {
  return exts.map((ext) => {
    const view = views[ext.name];
    if (!view || ext.type !== "node") return ext;
    return (ext as Node).extend({ addNodeView: () => view });
  });
}

export function EDIT_ONLY(_host: EditorHost, stores: EditorStores): Extensions {
  return [
    StructureKeymap,
    FormatKeymap,
    KlarityInputRules,
    KlarityPaste,
    BlockHandle,
    MoveBlockKeymap,
    SlashCommand.configure({ store: stores.slash }),
    DocLinkSuggest.configure({ store: stores.links }),
    UndoRedo,
    Dropcursor.configure({ color: "#E8873A", width: 2 }),
    Gapcursor,
    // Renamed: the schema already has a node called "placeholder" (the [[Label]] field).
    Placeholder.extend({ name: "klarityEmptyHint" }).configure({
      dataAttribute: "hint",
      placeholder: ({ node }) => (node.type.name === "paragraph" ? "Type / for blocks, @ to link a document" : ""),
    }),
  ];
}

export function editorExtensions(opts: { mode: "read" | "edit"; host: EditorHost; stores?: EditorStores }): Extensions {
  const base = withNodeViews(klarityExtensions, NODE_VIEWS);
  const present = new Set(base.map((e) => e.name));
  const extra: AnyExtension[] = [HostExtension.configure({ host: opts.host })];
  if (opts.mode === "edit") extra.push(...EDIT_ONLY(opts.host, opts.stores ?? createEditorStores()));
  return [...base, ...extra.filter((e) => !present.has(e.name))];
}
